import React, { useEffect, useState } from "react";
import { Check, Copy, Gift } from "lucide-react";
import api from "../../lib/api";
import { secondaryBtn } from "./PortalShell";
import { NOBODY_YET, offerParts, progressParts, stateParts, unpaidLine } from "../../lib/referralPortal";

/** Text parts, the bold ones in bold. The team's benefit text is text, never markup. */
function Parts({ parts }) {
  return parts.map(([text, bold], i) => (bold ? <strong key={i} className="text-[#373A45]">{text}</strong> : <span key={i}>{text}</span>));
}

/**
 * "Recomandările tale" — the participant's referral code in the portal
 * (13.10.2026): the code and its link, how many colleagues enrolled with it,
 * the names of those who paid and the number of those who have not.
 *
 * Renders nothing unless the server says so: the setting is off by default,
 * and a participant without a code sees no card.
 */
export default function ReferralCard() {
  const [card, setCard] = useState(null);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    let gone = false;
    api.get("/portal/referral")
      .then((r) => { if (!gone) setCard(r.data); })
      .catch(() => { if (!gone) setCard({ show: false }); });
    return () => { gone = true; };
  }, []);

  if (!card?.show) return null;

  const copy = async (what) => {
    try {
      await navigator.clipboard.writeText(what === "code" ? card.code : card.link);
      setCopied(what);
    } catch {
      setCopied("");
    }
  };

  const paid = card.paid || [];
  const unpaid = unpaidLine(card.unpaid);

  return (
    <section className="mt-8 rounded-2xl border border-[#E5E7EB] bg-white p-5" aria-labelledby="portal-referral"
      data-testid="portal-referral-card">
      <div className="flex items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#965EA7]/10 text-[#965EA7]">
          <Gift size={17} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 id="portal-referral" className="font-display text-lg font-bold text-[#373A45]">Recomandările tale</h2>
          <p className="mt-1 text-sm text-[#373A45]">
            Codul tău de recomandare:{" "}
            <strong className="font-mono text-base tracking-widest text-[#7A4986]" data-testid="portal-referral-code">{card.code}</strong>
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => copy("code")} className={secondaryBtn} data-testid="portal-referral-copy-code">
              {copied === "code" ? <Check size={14} /> : <Copy size={14} />} Copiază codul
            </button>
            <button type="button" onClick={() => copy("link")} className={secondaryBtn} data-testid="portal-referral-copy-link">
              {copied === "link" ? <Check size={14} /> : <Copy size={14} />} Copiază linkul
            </button>
            <span className="text-xs text-[#2F6B4E]" aria-live="polite" data-testid="portal-referral-copied">
              {copied === "code" ? "Cod copiat." : copied === "link" ? "Link copiat." : ""}
            </span>
          </div>

          <p className="mt-4 text-sm leading-relaxed text-[#6B7280]" data-testid="portal-referral-offer">
            <Parts parts={offerParts(card.required, card.benefit)} />
          </p>
          <p className="mt-3 text-sm text-[#6B7280]" data-testid="portal-referral-progress">
            <Parts parts={progressParts(paid.length, card.required)} />
          </p>
          {paid.length > 0 && (
            <ul className="mt-2 grid gap-1 text-sm text-[#373A45]" data-testid="portal-referral-paid">
              {paid.map((name, i) => (
                <li key={`${name}-${i}`}>{name} <span className="text-[#6B7280]">· și-a plătit locul</span></li>
              ))}
            </ul>
          )}
          {unpaid && <p className="mt-2 text-sm text-[#6B7280]" data-testid="portal-referral-unpaid">{unpaid}</p>}
          {!paid.length && !unpaid && <p className="mt-2 text-sm text-[#6B7280]" data-testid="portal-referral-nobody">{NOBODY_YET}</p>}

          <p className="mt-4 border-t border-[#E5E7EB] pt-3 text-sm text-[#6B7280]" data-testid="portal-referral-state">
            <Parts parts={stateParts(card)} />
          </p>
        </div>
      </div>
    </section>
  );
}
